/**
 * Applications & keys page (Sprint 288 Stream E; Sprint 365 Stream B CHG-GW-001).
 *
 * Lists the tenant's gateway applications in a table (name · description ·
 * row actions). Keys and scopes live in a responsive overlay
 * (`ApplicationKeysDialog`). Mutations keep Keto gating:
 * - ``view``    → read applications and keys (the guard already enforces it)
 * - ``operate`` → rotate, suspend, resume
 * - ``admin``   → create/edit/archive applications, create/revoke keys
 *
 * The key secret is shown exactly once (creation/rotation) and is dropped from
 * state on close; internal ids are never rendered (ISS-634). There is no
 * ``DELETE /applications``: archiving uses ``PATCH {status: archived}`` with an
 * explicit confirmation (S365-DEBT-B1).
 */

import { useMemo, useState } from 'react';
import { Copy, KeyRound, Pencil, Plus, Trash2 } from 'lucide-react';
import { EmptyState, PageHeader } from '@vanguard-atlas/shared';
import { AppModal } from '@/components/ui/app-modal';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  SectionCard,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import { useI18n } from '@/i18n';
import { useModuleAccess } from '@/hooks/useModuleAccess';
import { ApplicationKeysDialog } from '../components/ApplicationKeysDialog';
import { ApplicationRowActions } from '../components/ApplicationRowActions';
import {
  useCreateApplication,
  useCreateKey,
  useGatewayApplications,
  useRevokeKey,
  useRotateKey,
  useSetKeyStatus,
  useUpdateApplication,
} from '../hooks/useGateway';
import type { GatewayApplication, GatewayKey } from '../types';

const AVAILABLE_SCOPES = [
  'models:invoke',
  'models:read',
  'embeddings:invoke',
  'responses:invoke',
  'messages:invoke',
  'agents:invoke',
  'usage:read',
] as const;

interface SecretReveal {
  name: string;
  secret: string;
}

export function GatewayDevelopersPage() {
  const { t } = useI18n();
  const canOperate = useModuleAccess('ai_gateway', 'operate');
  const canAdmin = useModuleAccess('ai_gateway', 'admin');

  const applicationsQuery = useGatewayApplications();
  const applications = useMemo(
    () => applicationsQuery.data?.data ?? [],
    [applicationsQuery.data],
  );

  const createKey = useCreateKey();
  const rotateKey = useRotateKey();
  const revokeKey = useRevokeKey();
  const setStatus = useSetKeyStatus();
  const createApplication = useCreateApplication();
  const updateApplication = useUpdateApplication();

  const [secretReveal, setSecretReveal] = useState<SecretReveal | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyScopes, setNewKeyScopes] = useState<string[]>(['models:invoke']);
  const [newAppName, setNewAppName] = useState('');
  const [confirmRevoke, setConfirmRevoke] = useState<GatewayKey | null>(null);
  const [keysApp, setKeysApp] = useState<GatewayApplication | null>(null);
  const [createKeyApp, setCreateKeyApp] = useState<GatewayApplication | null>(null);
  const [editApp, setEditApp] = useState<GatewayApplication | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [archiveApp, setArchiveApp] = useState<GatewayApplication | null>(null);

  function toggleScope(scope: string) {
    setNewKeyScopes((current) =>
      current.includes(scope) ? current.filter((s) => s !== scope) : [...current, scope],
    );
  }

  function openCreateKey(application: GatewayApplication) {
    setCreateKeyApp(application);
    setNewKeyName('');
    setNewKeyScopes(['models:invoke']);
    setCreateOpen(true);
  }

  async function submitCreateKey() {
    if (!createKeyApp || !newKeyName.trim() || newKeyScopes.length === 0) return;
    const created = await createKey.mutateAsync({
      application_id: createKeyApp.id,
      name: newKeyName.trim(),
      scopes: newKeyScopes,
    });
    setCreateOpen(false);
    setNewKeyName('');
    setNewKeyScopes(['models:invoke']);
    setSecretReveal({ name: created.name, secret: created.secret });
  }

  async function submitCreateApplication() {
    // ISS-705: el primer tenant puede no tener aplicaciones. Si el nombre está
    // vacío se usa un nombre por defecto para no dejar la acción bloqueada.
    const name = newAppName.trim() || t('gateway.applications.defaultName');
    const created = await createApplication.mutateAsync({ name });
    setNewAppName('');
    setKeysApp(created);
  }

  async function submitRotate(key: GatewayKey) {
    const rotated = await rotateKey.mutateAsync(key);
    setSecretReveal({ name: rotated.name, secret: rotated.secret });
  }

  async function submitRevoke() {
    if (!confirmRevoke) return;
    await revokeKey.mutateAsync(confirmRevoke);
    setConfirmRevoke(null);
  }

  function openEdit(application: GatewayApplication) {
    setEditApp(application);
    setEditName(application.name);
    setEditDescription(application.description ?? '');
  }

  async function submitEdit() {
    if (!editApp || !editName.trim()) return;
    await updateApplication.mutateAsync({
      applicationId: editApp.id,
      payload: { name: editName.trim(), description: editDescription.trim() || null },
    });
    setEditApp(null);
  }

  async function submitArchive() {
    if (!archiveApp) return;
    await updateApplication.mutateAsync({
      applicationId: archiveApp.id,
      payload: { status: 'archived' },
    });
    setArchiveApp(null);
  }

  return (
    <div className="p-6 space-y-6" data-testid="gateway-developers-page">
      <PageHeader
        icon={KeyRound}
        title={t('gateway.title')}
        subtitle={t('gateway.subtitle')}
        testId="gateway-developers-header"
      />

      <SectionCard
        icon={KeyRound}
        title={t('gateway.applications.title')}
        description={t('gateway.applications.description')}
        testId="applications-section"
      >
        <div className="space-y-4">
          {applicationsQuery.isLoading ? (
            <Spinner />
          ) : applicationsQuery.isError ? (
            <div
              className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
              role="alert"
              data-testid="applications-error"
            >
              <p>{t('gateway.applications.error')}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => void applicationsQuery.refetch()}
                data-testid="applications-retry"
              >
                {t('gateway.applications.retry')}
              </Button>
            </div>
          ) : applications.length === 0 ? (
            <EmptyState
              icon={KeyRound}
              title={t('gateway.applications.emptyTitle')}
              description={t('gateway.applications.empty')}
              testId="no-apps"
            />
          ) : (
            <Table data-testid="applications-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t('gateway.applications.columns.name')}</TableHead>
                  <TableHead>{t('gateway.applications.columns.description')}</TableHead>
                  <TableHead className="text-right">
                    {t('gateway.applications.columns.actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {applications.map((app) => (
                  <TableRow key={app.id} data-testid={`application-row-${app.id}`}>
                    <TableCell className="font-medium">
                      <button
                        type="button"
                        className="text-left hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={() => setKeysApp(app)}
                        data-testid={`application-open-${app.id}`}
                      >
                        {app.name}
                      </button>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {app.description || t('gateway.applications.noDescription')}
                    </TableCell>
                    <TableCell className="text-right">
                      <ApplicationRowActions
                        application={app}
                        canAdmin={canAdmin}
                        onEdit={openEdit}
                        onArchive={setArchiveApp}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {canAdmin && (
            <div className="flex flex-wrap items-end gap-2">
              <div className="w-full max-w-sm">
                <Label htmlFor="gateway-new-app">{t('gateway.applications.new')}</Label>
                <Input
                  id="gateway-new-app"
                  value={newAppName}
                  onChange={(event) => setNewAppName(event.target.value)}
                  placeholder={t('gateway.applications.namePlaceholder')}
                />
              </div>
              <Button
                onClick={() => void submitCreateApplication()}
                disabled={createApplication.isPending}
                data-testid="create-application"
              >
                <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
                {t('gateway.applications.create')}
              </Button>
            </div>
          )}
          {canAdmin && (
            <p className="text-xs text-muted-foreground" data-testid="application-default-hint">
              {t('gateway.applications.defaultHint')}
            </p>
          )}
          {createApplication.isError && (
            <p className="text-sm text-red-600" role="alert" data-testid="create-application-error">
              {t('gateway.applications.createError')}
            </p>
          )}
        </div>
      </SectionCard>

      <ApplicationKeysDialog
        application={keysApp}
        open={keysApp !== null}
        onClose={() => setKeysApp(null)}
        canOperate={canOperate}
        canAdmin={canAdmin}
        onCreateKey={openCreateKey}
        onRotate={(key) => void submitRotate(key)}
        onSetStatus={(key, action) => void setStatus.mutateAsync({ key, action })}
        onRevoke={setConfirmRevoke}
      />

      {createOpen && createKeyApp && (
        <AppModal
          onClose={() => setCreateOpen(false)}
          ariaLabel={t('gateway.create.title')}
          testId="create-key-dialog"
        >
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle>{t('gateway.create.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="new-key-name">{t('gateway.create.name')}</Label>
                <Input
                  id="new-key-name"
                  value={newKeyName}
                  onChange={(event) => setNewKeyName(event.target.value)}
                  placeholder={t('gateway.create.namePlaceholder')}
                />
              </div>
              <div>
                <Label>{t('gateway.create.scopes')}</Label>
                <div className="mt-2 space-y-1">
                  {AVAILABLE_SCOPES.map((scope) => (
                    <label key={scope} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={newKeyScopes.includes(scope)}
                        onChange={() => toggleScope(scope)}
                        data-testid={`scope-${scope}`}
                      />
                      <span className="font-mono text-xs">{scope}</span>
                    </label>
                  ))}
                </div>
              </div>
              {createKey.isError && (
                <p className="text-sm text-red-600" data-testid="create-key-error">
                  {t('gateway.create.error')}
                </p>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setCreateOpen(false)}>
                  {t('gateway.create.cancel')}
                </Button>
                <Button
                  onClick={() => void submitCreateKey()}
                  disabled={!newKeyName.trim() || newKeyScopes.length === 0 || createKey.isPending}
                  data-testid="submit-create-key"
                >
                  {t('gateway.create.submit')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </AppModal>
      )}

      {secretReveal && (
        <AppModal
          onClose={() => setSecretReveal(null)}
          ariaLabel={t('gateway.secret.title', { name: secretReveal.name })}
          testId="secret-dialog"
        >
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle>{t('gateway.secret.title', { name: secretReveal.name })}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-amber-700 dark:text-amber-300">
                {t('gateway.secret.warning')}
              </p>
              <div className="flex items-center gap-2">
                <code
                  className="flex-1 break-all rounded bg-gray-100 p-2 font-mono text-xs dark:bg-gray-800"
                  data-testid="secret-value"
                >
                  {secretReveal.secret}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void navigator.clipboard?.writeText(secretReveal.secret)}
                  data-testid="copy-secret"
                >
                  <Copy className="h-3 w-3 mr-1" aria-hidden="true" />
                  {t('gateway.secret.copy')}
                </Button>
              </div>
              <div className="flex justify-end">
                <Button onClick={() => setSecretReveal(null)} data-testid="close-secret">
                  {t('gateway.secret.close')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </AppModal>
      )}

      {confirmRevoke && (
        <AppModal
          onClose={() => setConfirmRevoke(null)}
          ariaLabel={t('gateway.revoke.title', { name: confirmRevoke.name })}
          testId="revoke-dialog"
          dismissible={false}
        >
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>{t('gateway.revoke.title', { name: confirmRevoke.name })}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {t('gateway.revoke.warning')}
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setConfirmRevoke(null)}>
                  {t('gateway.revoke.cancel')}
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => void submitRevoke()}
                  disabled={revokeKey.isPending}
                  data-testid="confirm-revoke"
                >
                  {t('gateway.revoke.confirm')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </AppModal>
      )}

      {editApp && (
        <AppModal
          onClose={() => setEditApp(null)}
          ariaLabel={t('gateway.applications.editTitle')}
          testId="edit-application-dialog"
        >
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Pencil className="h-4 w-4 text-primary" aria-hidden="true" />
                {t('gateway.applications.editTitle')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="edit-app-name">{t('gateway.applications.columns.name')}</Label>
                <Input
                  id="edit-app-name"
                  value={editName}
                  onChange={(event) => setEditName(event.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="edit-app-description">
                  {t('gateway.applications.columns.description')}
                </Label>
                <Input
                  id="edit-app-description"
                  value={editDescription}
                  onChange={(event) => setEditDescription(event.target.value)}
                />
              </div>
              {updateApplication.isError && (
                <p className="text-sm text-red-600" role="alert" data-testid="edit-application-error">
                  {t('gateway.applications.editError')}
                </p>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditApp(null)}>
                  {t('gateway.applications.cancel')}
                </Button>
                <Button
                  onClick={() => void submitEdit()}
                  disabled={!editName.trim() || updateApplication.isPending}
                  data-testid="submit-edit-application"
                >
                  {t('gateway.applications.save')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </AppModal>
      )}

      {archiveApp && (
        <AppModal
          onClose={() => setArchiveApp(null)}
          ariaLabel={t('gateway.applications.archiveTitle', { name: archiveApp.name })}
          testId="archive-application-dialog"
          dismissible={false}
        >
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trash2 className="h-4 w-4 text-red-600" aria-hidden="true" />
                {t('gateway.applications.archiveTitle', { name: archiveApp.name })}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {t('gateway.applications.archiveWarning')}
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setArchiveApp(null)}>
                  {t('gateway.applications.cancel')}
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => void submitArchive()}
                  disabled={updateApplication.isPending}
                  data-testid="confirm-archive-application"
                >
                  {t('gateway.applications.archiveConfirm')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </AppModal>
      )}
    </div>
  );
}
